import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./multiSelectDropdown.scss";
import { Button } from "../button/Button";
type Option = { label: string; value: string; disabled?: boolean };

type Props = {
  label?: string;
  placeholder?: string;

  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;

  searchable?: boolean;
  closeOnSelect?: boolean;
  selectAllMode?: "visible" | "all";
  maxSelectedSummary?: number;
  disabled?: boolean;

  placement?: "auto" | "bottom-start" | "bottom-end" | "top-start" | "top-end";
  portal?: boolean;
  zIndex?: number;

  className?: string;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function useOutsideClick(
  refs: React.RefObject<HTMLElement>[],
  onOutside: () => void,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const inside = refs.some((r) => r.current && r.current.contains(target));
      if (!inside) onOutside();
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [refs, onOutside, enabled]);
}

function getScrollableAncestors(el: HTMLElement | null): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node) {
    const style = getComputedStyle(node);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const canScroll =
      /(auto|scroll|overlay)/.test(overflowY) ||
      /(auto|scroll|overlay)/.test(overflowX);
    if (canScroll) ancestors.push(node);
    node = node.parentElement;
  }
  ancestors.push(document.documentElement);
  return ancestors;
}

type FloatingPos = {
  top: number;
  left: number;
  width: number;
  transformOrigin: string;
  actualPlacement: "bottom-start" | "bottom-end" | "top-start" | "top-end";
};

function computeFloatingPosition(opts: {
  anchorRect: DOMRect;
  panelEl: HTMLElement;
  placement: Props["placement"];
  gap: number;
  minWidth: number;
  viewportPadding: number;
}): FloatingPos {
  const { anchorRect, panelEl, placement, gap, minWidth, viewportPadding } =
    opts;

  const panelRect = panelEl.getBoundingClientRect();
  const panelW = Math.max(minWidth, panelRect.width);
  const panelH = panelRect.height;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceBelow = vh - anchorRect.bottom - viewportPadding;
  const spaceAbove = anchorRect.top - viewportPadding;

  const wantTop =
    placement?.startsWith("top") ||
    (placement === "auto" &&
      spaceAbove > spaceBelow &&
      spaceAbove >= Math.min(panelH, 240));

  const vertical = wantTop ? "top" : "bottom";

  const wantEnd =
    placement?.endsWith("end") ||
    (placement === "auto" && anchorRect.left > vw / 2);

  const horizontal = wantEnd ? "end" : "start";

  let top =
    vertical === "bottom"
      ? anchorRect.bottom + gap
      : anchorRect.top - gap - panelH;
  let left =
    horizontal === "start" ? anchorRect.left : anchorRect.right - panelW;

  left = clamp(left, viewportPadding, vw - viewportPadding - panelW);

  if (top < viewportPadding && vertical === "top" && spaceBelow > spaceAbove) {
    top = anchorRect.bottom + gap;
  }
  if (
    top + panelH > vh - viewportPadding &&
    vertical === "bottom" &&
    spaceAbove > spaceBelow
  ) {
    top = anchorRect.top - gap - panelH;
  }
  top = clamp(top, viewportPadding, vh - viewportPadding - panelH);

  const actualPlacement =
    `${vertical}-${horizontal}` as FloatingPos["actualPlacement"];
  const transformOrigin =
    vertical === "bottom"
      ? horizontal === "start"
        ? "top left"
        : "top right"
      : horizontal === "start"
        ? "bottom left"
        : "bottom right";

  return {
    top,
    left,
    width: Math.max(minWidth, anchorRect.width),
    transformOrigin,
    actualPlacement,
  };
}

export function MultiSelectDropdown({
  label,
  placeholder = "Select…",
  options,
  value,
  onChange,
  searchable = true,
  closeOnSelect = false,
  selectAllMode = "visible",
  maxSelectedSummary = 2,
  disabled = false,
  placement = "auto",
  portal = true,
  zIndex = 50_000,
  className,
}: Props) {
  const reactId = useId();
  const buttonId = `msd-btn-${reactId}`;
  const panelId = `msd-panel-${reactId}`;
  const labelId = `msd-label-${reactId}`;

  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [pos, setPos] = useState<FloatingPos | null>(null);

  const [draftValue, setDraftValue] = useState<string[]>(value);

  useEffect(() => {
    if (!open) setDraftValue(value);
  }, [value, open]);

  const enabledOptions = useMemo(
    () => options.filter((o) => !o.disabled),
    [options],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const filteredEnabled = useMemo(
    () => filtered.filter((o) => !o.disabled),
    [filtered],
  );

  const selectedSet = useMemo(() => new Set(draftValue), [draftValue]);

  const allTarget =
    selectAllMode === "visible" ? filteredEnabled : enabledOptions;

  const allSelected =
    allTarget.length > 0 && allTarget.every((o) => selectedSet.has(o.value));
  const noneSelected = allTarget.every((o) => !selectedSet.has(o.value));
  const indeterminate = !noneSelected && !allSelected;

  const selectedLabels = useMemo(() => {
    const map = new Map(options.map((o) => [o.value, o.label]));
    return draftValue.map((v) => map.get(v)).filter(Boolean) as string[];
  }, [draftValue, options]);

  const buttonText = useMemo(() => {
    if (selectedLabels.length === 0) return placeholder;
    if (selectedLabels.length === options.length) return placeholder;
    if (selectedLabels.length <= maxSelectedSummary)
      return selectedLabels.join(", ");
    return `${selectedLabels.length} selected`;
  }, [selectedLabels, options.length, placeholder, maxSelectedSummary]);

  const close = () => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
    setDraftValue(value);
    requestAnimationFrame(() => anchorRef.current?.focus());
  };

  const onDone = () => {
    onChange(draftValue);
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
    requestAnimationFrame(() => anchorRef.current?.focus());
  };

  useOutsideClick([anchorRef as any, panelRef as any], close, open);

  useEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const next = computeFloatingPosition({
        anchorRect: rect,
        panelEl: panel,
        placement,
        gap: 8,
        minWidth: 240,
        viewportPadding: 8,
      });
      setPos(next);
    };

    update();

    const ancestors = getScrollableAncestors(anchor);
    const onScrollOrResize = () => update();

    window.addEventListener("resize", onScrollOrResize);
    ancestors.forEach((a) =>
      a.addEventListener("scroll", onScrollOrResize, { passive: true }),
    );

    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      ancestors.forEach((a) =>
        a.removeEventListener("scroll", onScrollOrResize as any),
      );
    };
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      if (searchable) searchRef.current?.focus();
      else panelRef.current?.focus();
    });
  }, [open, searchable]);

  const toggleValue = (val: string) => {
    const next = selectedSet.has(val)
      ? draftValue.filter((v) => v !== val)
      : [...draftValue, val];
    setDraftValue(next);

    if (closeOnSelect) close();
  };

  const onSelectAll = () => {
    if (allSelected) {
      const targetSet = new Set(allTarget.map((o) => o.value));
      setDraftValue(draftValue.filter((v) => !targetSet.has(v)));
    } else {
      const toAdd = allTarget
        .map((o) => o.value)
        .filter((v) => !selectedSet.has(v));
      setDraftValue([...draftValue, ...toAdd]);
    }
  };

  const onClearAll = () => setDraftValue([]);

  const onKeyDownTrigger = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onKeyDownPanel = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }

    const items = filtered;
    if (items.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(items.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = items[activeIndex];
      if (opt && !opt.disabled) toggleValue(opt.value);
    }
  };

  const panel = (
    <div
      ref={panelRef}
      id={panelId}
      role="listbox"
      aria-labelledby={label ? labelId : undefined}
      aria-multiselectable="true"
      tabIndex={-1}
      className="msd-panel"
      style={{
        position: portal ? "fixed" : "absolute",
        top: pos ? pos.top - 8 : 106,
        left: pos?.left ?? 0,
        width: pos?.width ?? 240,
        zIndex,
        transformOrigin: pos?.transformOrigin ?? "top left",
      }}
      onKeyDown={onKeyDownPanel}
    >
      <div className="msd-header">
        {searchable && (
          <input
            ref={searchRef}
            className="msd-search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder="Search…"
            aria-label="Search options"
          />
        )}

        <div className="msd-actions">
          <button type="button" style={{outline: "none"}} className="msd-actionBtn" onClick={onSelectAll}>
            <span
              className="msd-checkbox"
              aria-hidden="true"
              data-state={
                allSelected ? "checked" : indeterminate ? "mixed" : "unchecked"
              }
            />
            <Button
              color="primary"
              variant="plain"
              id="btn-apply"
              style={{ fontSize: 13, border: "none", outline: "none" }}
            >
              {allSelected ? "Unselect all" : "Select all"}
            </Button>
            {/* <span>{allSelected ? "Unselect all" : "Select all"}</span> */}
          </button>

          <Button
            color="primary"
            variant="plain"
            id="btn-apply"
            style={{ fontSize: 13, border: "none", outline: "none" }}
            onClick={onClearAll}
            // disabled={draftValue.length === 0}
          >
            Deselect All
          </Button>
          {/* <button
            type="button"
            className="msd-linkBtn"
            onClick={onClearAll}
            disabled={draftValue.length === 0}
          >
            Clear
          </button> */}
        </div>
      </div>

      <div className="msd-list" role="presentation">
        {filtered.length === 0 ? (
          <div className="msd-empty">No results</div>
        ) : (
          filtered.map((opt, idx) => {
            const checked = selectedSet.has(opt.value);
            const isActive = idx === activeIndex;

            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={checked}
                aria-disabled={opt.disabled || undefined}
                className={[
                  "msd-item",
                  checked ? "is-checked" : "",
                  isActive ? "is-active" : "",
                  opt.disabled ? "is-disabled" : "",
                ].join(" ")}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => !opt.disabled && toggleValue(opt.value)}
              >
                <span
                  className="msd-checkbox"
                  aria-hidden="true"
                  data-state={checked ? "checked" : "unchecked"}
                />
                <span className="msd-itemLabel">{opt.label}</span>
              </div>
            );
          })
        )}
      </div>

      <div className="msd-footer">
        <span className="msd-count">{draftValue.length} selected</span>
        <Button
          color="primary"
          variant="solid"
          type="button"
          id="btn-apply"
          style={{
            fontSize: 13,
            border: "none",
            outline: "none",
            padding: "5px 10px",
          }}
          onClick={onDone}
          // disabled={draftValue.length === 0}
        >
          APPLY
        </Button>
        {/* <button
          type="button"
          className="msd-submitBtn"
          onClick={onDone}
        ></button> */}
      </div>
    </div>
  );
  return (
    <div className={["msd", className ?? ""].join(" ")}>
      {label && (
        <div id={labelId} className="msd-label">
          {label}
        </div>
      )}

      <button
        ref={anchorRef}
        id={buttonId}
        type="button"
        className="msd-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => {
            const next = !v;
            if (next) setDraftValue(value);
            return next;
          });
        }}
        onKeyDown={onKeyDownTrigger}
      >
        <span
          className={
            value.length ? "msd-triggerText" : "msd-triggerText is-placeholder"
          }
        >
          {buttonText}
        </span>
        <span aria-hidden="true" className="msd-caret">
          ▾
        </span>
      </button>

      {open && (portal ? createPortal(panel, document.body) : panel)}
    </div>
  );
}
